import { fetchPatentsFromS3 } from '../../api/fetch-patents-from-s3'
import { isNull } from 'lodash'
import { processFamily } from './process-family-tree'
import { z } from 'zod'

export const FamilyResultSchema = z.object({
  documentNumber: z.string(),
  dateProduced: z.string(),
  familyId: z.string().nullable().default(null),
  inventionTitle: z.string().optional(),
  internationalConventionData: z
    .object({
      pctOrRegionalPublishingData: z
        .object({
          docdb_document_number: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  priorityClaims: z
    .array(
      z.object({
        dataFormat: z.string().optional(),
        documentDate: z.string().optional(),
        documentNumber: z.string(),
        loadSource: z.string().optional(),
        country: z.string().optional(),
        kind: z.string().optional(),
      })
    )
    .optional(),
  publicationReferences: z
    .array(
      z.object({
        country: z.string().optional(),
        documentDate: z.string().optional(),
        documentNumber: z.string(),
        kind: z.string().optional(),
      })
    )
    .optional(),
  publicationReference: z.object({
    country: z.string(),
    documentDate: z.string(),
    documentNumber: z.string(),
    kind: z.string().optional(),
  }),
  applicationReference: z.object({
    documentNumber: z.string(),
    documentDate: z.string().optional(),
    country: z.string(),
    kind: z.string().optional(),
  }),
  applicationReferences: z.array(
    z.object({
      documentNumber: z.string().optional(),
      documentDate: z.string().optional(),
      country: z.string().optional(),
      dataFormat: z.string().optional(),
    })
  ),
  inpadocFamilyId: z.string().optional(),
  inpadocFamilyMembers: z
    .array(
      z.object({
        documentNumber: z.string(),
        kind: z.string(),
        documentDate: z.string(),
        country: z.string(),
        dataFormat: z.string().optional(),
      })
    )
    .optional(),
  minPriorityDate: z.string().optional(),
  projectedExpirationDate: z.string().optional(),
  publicationType: z.string(),
  relatedDocuments: z
    .array(
      z.object({
        dataFormat: z.string().optional(),
        relationType: z.string(),
        relatedDocuments: z
          .array(
            z.object({
              country: z.string(),
              documentNumber: z.string().optional(),
              relatedDocumentType: z.enum([
                'parent-doc',
                'child-doc',
                'parent-grant-doc',
                'parent-pct-doc',
              ]),
            })
          )
          .optional(),
      })
    )
    .optional(),
  abstractParagraphs: z
    .array(
      z.object({
        dataFormat: z.string().optional(),
        lang: z.string(),
        loadSource: z.string().optional(),
        plainText: z.string(),
      })
    )
    .optional(),
})

export type FamilyResult = z.infer<typeof FamilyResultSchema>

export interface FamilyNode {
  document: FamilyResult
  children: FamilyNode[]
}

export interface FamilyTree {
  rootDocuments: FamilyNode[]
}

export interface ProcessedFamilyNode {
  id: string
  data: {
    documentNumber: string
    country?: string
    applicationNumber?: string
    applicationCountry?: string
    publishNumber?: string
    publishCountry?: string
    priorityDate?: string
    publishedDate?: string
    issuedDate?: string
  }
  patent?: FamilyResult
  parentIds: string[]
  level: number
  nodeType: 'priority' | 'publication' | 'grant'
}

export interface Connection {
  source: string
  target: string
  label?: string
}

export interface ProcessedFamilyTree {
  nodes: ProcessedFamilyNode[]
  connections: readonly Connection[]
}

export const getFamilyTree = async (
  documents: Array<{
    jurisdiction: string
    publicationDate: string | null
    kind: string
    documentNumber: string
  }>,
  credentials: {
    accessKeyId: string
    secretAccessKey: string
  }
): Promise<ProcessedFamilyTree> => {
  const results = await fetchPatentsFromS3(documents, credentials)
  const parsedResults = results
    .filter((result) => !isNull(result))
    .map((result) => FamilyResultSchema.parse(result))
  return processFamily(parsedResults)
}
